import React, { useEffect, useState } from 'react';
import api from '../../../config/axios';
import Swal from 'sweetalert2';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';

import ComandaAbertaModal from './modals/ComandaAberta';
import ComandaFechadaModal from './modals/ComandaFechada';
import ComandaCanceladaModal from './modals/ComandaCancelada';
import DatePicker from '../../../components/utils/datepicker';

import { FaPlus, FaEdit, FaFilter, FaTimes } from 'react-icons/fa';
import Paginacao from '../../../components/utils/paginacao';

const Comandas = () => {
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('abertas');

    const [comandas, setComandas] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [dataFiltro, setDataFiltro] = useState(null);
    const [dataAberturaFiltro, setDataAberturaFiltro] = useState(null);

    const [selectedComanda, setSelectedComanda] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [valorTotal, setValorTotal] = useState(0);
    const limit = 10;

    const [searchParams] = useSearchParams();

    const fetchComandas = async (currentPage) => {
        try {
            Swal.fire({
                title: "Carregando...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            let status;
            if (activeTab === 'abertas') {
                status = 'aberta';
            } else if (activeTab === 'finalizadas') {
                status = 'fechada';
            } else if (activeTab === 'canceladas') {
                status = 'cancelada';
            }

            let url = `/comandas?page=${currentPage}&limit=${limit}&status=${status}&search=${filtro}`;

            if (activeTab === 'finalizadas') {
                if (dataAberturaFiltro) {
                    const dataFormatada = moment(dataAberturaFiltro).format('YYYY-MM-DD');
                    url += `&data_abertura=${dataFormatada}`;
                }
                if (dataFiltro) {
                    const dataFormatada = moment(dataFiltro).format('YYYY-MM-DD');
                    url += `&data_finalizada=${dataFormatada}`;
                }
            } else {
                if (dataFiltro) {
                    const dataFormatada = moment(dataFiltro).format('YYYY-MM-DD');

                    if (activeTab === 'abertas') {
                        url += `&data_abertura=${dataFormatada}`;
                    } else if (activeTab === 'canceladas') {
                        url += `&data_cancelada=${dataFormatada}`;
                    }
                }
            }

            const response = await api.get(url);

            setError(null);
            setComandas(response.data.records || []);
            setTotalPages((Math.ceil(response.data.totalRegistros / limit)) || 1);
            setTotalRegistros(response.data.totalRegistros || 0);
            setValorTotal(response.data.valorTotal || 0);
            Swal.close();
        } catch (err) {
            Swal.close();
            setError('Erro ao carregar a lista de comandas.');
        }
    };

    const fetchComandasNoLoading = async (currentPage) => {
        try {
            let status;
            if (activeTab === 'abertas') {
                status = 'aberta';
            } else if (activeTab === 'finalizadas') {
                status = 'fechada';
            } else if (activeTab === 'canceladas') {
                status = 'cancelada';
            }

            let url = `/comandas?page=${currentPage}&limit=${limit}&status=${status}&search=${filtro}`;

            if (activeTab === 'finalizadas') {
                if (dataAberturaFiltro) {
                    const dataFormatada = moment(dataAberturaFiltro).format('YYYY-MM-DD');
                    url += `&data_abertura=${dataFormatada}`;
                }
                if (dataFiltro) {
                    const dataFormatada = moment(dataFiltro).format('YYYY-MM-DD');
                    url += `&data_finalizada=${dataFormatada}`;
                }
            } else {
                if (dataFiltro) {
                    const dataFormatada = moment(dataFiltro).format('YYYY-MM-DD');

                    if (activeTab === 'abertas') {
                        url += `&data_abertura=${dataFormatada}`;
                    } else if (activeTab === 'canceladas') {
                        url += `&data_cancelada=${dataFormatada}`;
                    }
                }
            }

            const response = await api.get(url);

            setComandas(response.data.records || []);
            setTotalPages((Math.ceil(response.data.totalRegistros / limit)) || 1);
            setTotalRegistros(response.data.totalRegistros || 0);
            setValorTotal(response.data.valorTotal || 0);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar a lista de comandas.');
        }
    };

    useEffect(() => {
        fetchComandas(currentPage);
    }, [currentPage, activeTab]);

    useEffect(() => {
        const comandaId = searchParams.get('comanda');
        const tab = searchParams.get('tab');

        if (tab && (tab === 'abertas' || tab === 'finalizadas' || tab === 'canceladas')) {
            setActiveTab(tab);
        }

        if (comandaId) {
            handleOpenComandaFromAgendamento(comandaId);
        }
    }, []);

    const handlePageChange = (currentPage) => {
        setCurrentPage(currentPage);
    };

    const handleFiltrar = () => {
        setCurrentPage(1);
        fetchComandas(1);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setFiltro('');
        setDataFiltro(null);
        setDataAberturaFiltro(null);
    };

    const handleAddComanda = () => {
        setSelectedComanda(null);
        setIsModalOpen(true);
    };

    const handleEditComanda = (comanda) => {
        const data = {
            id: comanda.id,
            cliente: {
                label: comanda.cliente.nome,
                value: comanda.id_pessoa_cliente
            },
            data_abertura: moment(comanda.data_abertura).format('DD/MM/YYYY HH:mm:ss'),
            data_finalizada: activeTab === 'finalizadas' ? moment(comanda.data_finalizada).format('DD/MM/YYYY HH:mm:ss') : undefined,
            data_cancelada: activeTab === 'canceladas' ? moment(comanda.data_cancelada).format('DD/MM/YYYY HH:mm:ss') : undefined,
            status: comanda.status,
            observacao: comanda.observacao,
            caixa_responsavel: comanda.id_caixa,
            itens: [],
            pagamentos: comanda.pagamentos,
            criado_por: comanda.criado_por,
            criado_em: comanda.criado_em,
            alterado_por: comanda.alterado_por,
            alterado_em: comanda.alterado_em
        }

        for (let i = 0; i < comanda.itens.length; i++) {
            const item = comanda.itens[i];

            const novo = {
                id: item.id,
                id_item: item.id_item,
                tipo_item: item.tipo_item,
                qtd: item.qtd,
                profissional: item.colaborador ? {
                    label: item.colaborador.nome,
                    value: item.id_pessoa_colaborador
                } : null,
                assistentes: (item.assistentes || []).map(assistente => ({
                    id: assistente.id,
                    profissional: assistente.profissional
                })),
                valor_item: item.valor_item,
                desconto_item: item.desconto_item + '%',
                final_item: parseFloat(item.valor_item) - (parseFloat(item.valor_item) * (parseFloat(item.desconto_item) / 100)),
                editar: false,
            }

            if (item.tipo_item === 'caixinha') {
                novo.servico = '';
                novo.produto = '';
            } else if (item.servico) {
                novo.servico = {
                    label: item.servico.nome,
                    value: item.servico.id_servico,
                    preco: item.servico.preco
                }
            } else {
                novo.produto = {
                    label: item.produto.nome,
                    value: item.produto.id_produto,
                    preco: item.produto.valor_venda
                }
            }
            data.itens.push(novo);
        }
        setSelectedComanda(data);
        setIsModalOpen(true);
    };

    const calcularTotal = (itens) => {
        const total = itens.reduce((total, item) => {
            const valorFinal = parseFloat(item.valor_item) - (parseFloat(item.valor_item) * (parseFloat(item.desconto_item) / 100));
            return total + valorFinal;
        }, 0);

        return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const handleOpenComandaFromAgendamento = async (comandaId) => {
        try {
            const response = await api.get(`/comandas/${comandaId}`);

            if (response.data) {
                const comandaStatus = response.data.status;
                if (comandaStatus === 'aberta') {
                    setActiveTab('abertas');
                } else if (comandaStatus === 'fechada') {
                    setActiveTab('finalizadas');
                } else if (comandaStatus === 'cancelada') {
                    setActiveTab('canceladas');
                }

                handleEditComanda(response.data);
            } else {
                Swal.fire('Erro', 'Não foi possível carregar os dados da comanda', 'error');
            }
        } catch (error) {
            Swal.fire('Erro', 'Não foi possível carregar os dados da comanda', 'error');
        }
    };

    const renderTableHeaders = () => {
        if (activeTab === 'abertas') {
            return (
                <tr>
                    <th>Comanda</th>
                    <th>Cliente</th>
                    <th>Data de Abertura</th>
                    <th>Valor</th>
                    <th className="text-center">Ações</th>
                </tr>
            );
        } else if (activeTab === 'finalizadas') {
            return (
                <tr>
                    <th>Comanda</th>
                    <th>Cliente</th>
                    <th>Data de Abertura</th>
                    <th>Data de Finalização</th>
                    <th>Valor</th>
                    <th className="text-center">Ações</th>
                </tr>
            );
        } else if (activeTab === 'canceladas') {
            return (
                <tr>
                    <th>Comanda</th>
                    <th>Cliente</th>
                    <th>Data de Abertura</th>
                    <th>Data de Finalização</th>
                    <th>Data de Cancelamento</th>
                    <th>Valor</th>
                    <th className="text-center">Ações</th>
                </tr>
            );
        }
    };

    const renderTableRow = (comanda) => {
        if (activeTab === 'abertas') {
            return (
                <tr key={comanda.id}>
                    <td className="text-nowrap">{comanda.id}</td>
                    <td className="text-nowrap">{comanda.cliente.nome}</td>
                    <td className="text-nowrap">{moment(comanda.data_abertura).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td className="text-nowrap">{calcularTotal(comanda.itens)}</td>
                    <td className="text-center text-nowrap">
                        <button
                            className="btn btn-custom-secondary btn-sm"
                            onClick={() => handleEditComanda(comanda)}
                        >
                            <FaEdit />
                        </button>
                    </td>
                </tr>
            );
        } else if (activeTab === 'finalizadas') {
            return (
                <tr key={comanda.id}>
                    <td className="text-nowrap">{comanda.id}</td>
                    <td className="text-nowrap">{comanda.cliente.nome}</td>
                    <td className="text-nowrap">{moment(comanda.data_abertura).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td className="text-nowrap">{moment(comanda.data_finalizada).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td className="text-nowrap">{calcularTotal(comanda.itens)}</td>
                    <td className="text-center text-nowrap">
                        <button
                            className="btn btn-custom-secondary btn-sm"
                            onClick={() => handleEditComanda(comanda)}
                            title="Ver Detalhes"
                        >
                            <FaEdit />
                        </button>
                    </td>
                </tr>
            );
        } else if (activeTab === 'canceladas') {
            return (
                <tr key={comanda.id}>
                    <td className="text-nowrap">{comanda.id}</td>
                    <td className="text-nowrap">{comanda.cliente.nome}</td>
                    <td className="text-nowrap">{moment(comanda.data_abertura).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td className="text-nowrap">{moment(comanda.data_finalizada).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td className="text-nowrap">{moment(comanda.data_cancelada).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td className="text-nowrap">{calcularTotal(comanda.itens)}</td>
                    <td className="text-center text-nowrap">
                        <button
                            className="btn btn-custom-secondary btn-sm"
                            onClick={() => handleEditComanda(comanda)}
                            title="Ver Detalhes"
                        >
                            <FaEdit />
                        </button>
                    </td>
                </tr>
            );
        }
    };

    const getEmptyMessage = () => {
        if (activeTab === 'abertas') {
            return 'Não há comandas abertas para exibir.';
        } else if (activeTab === 'finalizadas') {
            return 'Não há comandas finalizadas para exibir.';
        } else if (activeTab === 'canceladas') {
            return 'Não há comandas canceladas para exibir.';
        }
    };

    return (
        <div className="container-fluid">
            <div className="card">
                <div className="card-body">
                    <div className="row mt-3">
                        <div className="col-12">
                            <h4 className="fw-bold">Comandas</h4>
                        </div>
                    </div>

                    <ul className="nav nav-tabs mt-3">
                        <li className="nav-item">
                            <a
                                className={activeTab === 'abertas' ? 'nav-link active' : 'nav-link'}
                                aria-current="page"
                                onClick={() => handleTabChange('abertas')}
                                style={{ cursor: 'pointer' }}
                            >
                                Abertas
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={activeTab === 'finalizadas' ? 'nav-link active' : 'nav-link'}
                                aria-current="page"
                                onClick={() => handleTabChange('finalizadas')}
                                style={{ cursor: 'pointer' }}
                            >
                                Finalizadas
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={activeTab === 'canceladas' ? 'nav-link active' : 'nav-link'}
                                aria-current="page"
                                onClick={() => handleTabChange('canceladas')}
                                style={{ cursor: 'pointer' }}
                            >
                                Canceladas
                            </a>
                        </li>
                    </ul>

                    <div className="row mt-3 mb-3 d-flex align-items-center">
                        <div className="col-md-6 col-12">
                            {activeTab === 'abertas' && (
                                <button className="btn btn-outline-primary" onClick={handleAddComanda}>
                                    <FaPlus /> Abrir Comanda
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="row mt-4 mb-4">
                        {activeTab === 'finalizadas' ? (
                            <>
                                <div className="col-lg-3 col-12">
                                    <label className="form-label fw-bold">Data de Abertura</label>
                                    <DatePicker
                                        selected={dataAberturaFiltro}
                                        onChange={(date) => setDataAberturaFiltro(date)}
                                    />
                                </div>
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Data de Finalização</label>
                                    <DatePicker
                                        selected={dataFiltro}
                                        onChange={(date) => setDataFiltro(date)}
                                    />
                                </div>
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Buscar</label>
                                    <input
                                        type="text"
                                        placeholder="Buscar comanda"
                                        autoComplete="off"
                                        value={filtro}
                                        className="form-control"
                                        onChange={(e) => setFiltro(e.target.value.toUpperCase())}
                                    />
                                </div>
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3 d-flex align-items-end">
                                    <div className="d-flex gap-2 w-100 justify-content-end">
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setDataAberturaFiltro(null);
                                                setDataFiltro(null);
                                                setFiltro('');
                                            }}
                                        >
                                            Limpar
                                        </button>
                                        <button
                                            className="btn btn-custom-primary"
                                            onClick={handleFiltrar}
                                        >
                                            <FaFilter /> Filtrar
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="col-lg-3 col-12">
                                    <label className="form-label fw-bold">
                                        {activeTab === 'abertas' ? 'Data de Abertura' : 'Data de Cancelamento'}
                                    </label>
                                    <DatePicker
                                        selected={dataFiltro}
                                        onChange={(date) => setDataFiltro(date)}
                                    />
                                </div>
                                <div className="col-lg-3 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Buscar</label>
                                    <input
                                        type="text"
                                        placeholder="Buscar comanda"
                                        autoComplete="off"
                                        value={filtro}
                                        className="form-control"
                                        onChange={(e) => setFiltro(e.target.value.toUpperCase())}
                                    />
                                </div>
                                <div className="col-lg-6 col-12 mt-lg-0 mt-3 d-flex align-items-end">
                                    <div className="d-flex gap-2 w-100 justify-content-end">
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setDataFiltro(null);
                                                setFiltro('');
                                            }}
                                        >
                                            Limpar
                                        </button>
                                        <button
                                            className="btn btn-custom-primary"
                                            onClick={handleFiltrar}
                                        >
                                            <FaFilter /> Filtrar
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {error ? (
                        <p className="error-message">{error}</p>
                    ) : comandas.length === 0 ? (
                        <p className="text-center my-5">{getEmptyMessage()}</p>
                    ) : (
                        <>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <span>
                                        Valor Total: <strong>{(valorTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
                                    </span>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-striped">
                                    <thead className="table-custom-primary">
                                        {renderTableHeaders()}
                                    </thead>
                                    <tbody>
                                        {comandas.map((comanda) => renderTableRow(comanda))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="mb-3">
                            <span>
                                Total de registros: <strong>{totalRegistros}</strong>
                            </span>
                        </div>
                        <Paginacao
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>

                {isModalOpen && activeTab === 'abertas' && (
                    <ComandaAbertaModal
                        comanda={selectedComanda}
                        onClose={() => setIsModalOpen(false)}
                        onComandaAtualizada={() => fetchComandasNoLoading(currentPage)}
                    />
                )}

                {isModalOpen && activeTab === 'finalizadas' && (
                    <ComandaFechadaModal
                        comanda={selectedComanda}
                        onClose={() => setIsModalOpen(false)}
                        onComandaAtualizada={() => fetchComandas(currentPage)}
                    />
                )}

                {isModalOpen && activeTab === 'canceladas' && (
                    <ComandaCanceladaModal
                        comanda={selectedComanda}
                        onClose={() => setIsModalOpen(false)}
                    />
                )}
            </div>
            <div style={{ height: '50px' }}></div>
        </div>
    );
};

export default Comandas; 